import React, { Ref, useEffect } from "react";
import { Date_dateStringIsValid, Date_toDateString } from "../common/dateUtils";

type UseDateInputProps = {
    date: Date;
    ref: React.RefObject<HTMLInputElement>;
    comparer: (d0: Date, d1: Date) => boolean;
    stringParser?: (dateString: string) => Date;
    toStringValue: (date: Date) => string;
    onDateChange: (updatedDate: Date) => void;
};

export type DateManager = {
    ref: React.RefObject<HTMLInputElement>;
    date: Date;
    blurHandler: () => void;
};

function useDateInput({
    date,
    ref,
    comparer,
    stringParser,
    toStringValue,
    onDateChange
}: UseDateInputProps): DateManager {
    useEffect(() => {
        if (!ref.current) {
            return;
        }

        if (!Date_dateStringIsValid(ref.current.value)) {
            ref.current.value = toStringValue(date);
            return;
        }

        let uiValue = new Date(ref.current.value);
        if (stringParser) {
            uiValue = stringParser(ref.current.value);
        }

        if (!comparer(uiValue, date)) {
            ref.current.value = toStringValue(date);
        }
    }, [date.getTime()]);

    function dateBlurHandler() {
        if (!ref.current) {
            return;
        }

        let updatedValue: Date = stringParser ? stringParser(ref.current.value) : new Date(ref.current.value);

        const invalidValue: boolean = isNaN(updatedValue.getTime());
        if (invalidValue) {
            ref.current.value = toStringValue(date);
            return;
        }

        if (comparer(updatedValue, date)) {
            return;
        }

        onDateChange(updatedValue);
    }

    return {
        ref,
        date,
        blurHandler: dateBlurHandler
    };
}

export default useDateInput;
